import React, { useState, useEffect } from 'react';
import { X, Save, Type, Link as LinkIcon, FileText } from 'lucide-react';
import { Note, CreateNoteRequest, NoteType } from '../../types/note';

interface NoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (note: CreateNoteRequest) => Promise<void>;
    note?: Note | null;
}

export const NoteModal: React.FC<NoteModalProps> = ({
    isOpen,
    onClose,
    onSave,
    note
}) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState<NoteType>('text');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (note) {
            setTitle(note.title);
            // Editing from a list item: fetch full content first (list ships
            // preview only). For huge notes warn but still allow editing.
            if (note.content !== undefined) {
                setContent(note.content);
            } else if (note.preview !== undefined) {
                setContent(note.preview);
                if ((note.size ?? 0) > 262144) {
                    setError('Note is large — showing preview only; saving will overwrite the full content');
                }
            } else {
                setContent('');
            }
            setType(note.type);
        } else {
            setTitle('');
            setContent('');
            setType('text');
        }
        setError(null);
    }, [note, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) {
            setError('Title and content are required');
            return;
        }

        if (type === 'url') {
            try {
                new URL(content);
            } catch (e) {
                setError('Please enter a valid URL (including http:// or https://)');
                return;
            }
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await onSave({ title, content, type });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save note');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg transform transition-all animate-in fade-in zoom-in duration-200 overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {note ? 'Edit Note' : 'New Note'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col p-4 overflow-y-auto">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-100 dark:border-red-900/30">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Title Input */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Title
                            </label>
                            <input
                                id="title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Give your note a title"
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-gray-100 text-base"
                                autoFocus
                            />
                        </div>

                        {/* Type Selector */}
                        <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-900 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setType('text')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${type === 'text'
                                        ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400'
                                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                            >
                                <Type size={18} />
                                Text
                            </button>
                            <button
                                type="button"
                                onClick={() => setType('markdown')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${type === 'markdown'
                                        ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400'
                                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                            >
                                <FileText size={18} />
                                Markdown
                            </button>
                            <button
                                type="button"
                                onClick={() => setType('url')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${type === 'url'
                                        ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400'
                                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                            >
                                <LinkIcon size={18} />
                                URL
                            </button>
                        </div>

                        {/* Content Textarea */}
                        <div className="flex-1 flex flex-col">
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {type === 'url' ? 'URL' : type === 'markdown' ? 'Content (Markdown)' : 'Content'}
                            </label>
                            <textarea
                                id="content"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder={type === 'url' ? 'https://example.com' : type === 'markdown' ? '# Heading\n\n**bold**, *italic*, [link](https://example.com), lists, `code`…' : 'Your note content here...'}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-gray-100 text-base resize-none min-h-[150px]"
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={18} />
                            {isSubmitting ? 'Saving...' : 'Save Note'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
